<template>
  <div class="active-challenge-dashboard">
    <riseup-header/>
    <div class="top">
      <lottie-player class="celebration-animation" v-if="showCelebration"
                     :src="JSON.stringify(require('@/assets/challenge/loties/Conffetti 02.json'))" :autoplay="true"/>
      <div class="header">
        <img v-if="activeParticipant" class="icon" :src="require(`@/assets/challenge/${participantIcon}.svg`)"/>
        <div class="headline">{{ headline }}</div>
        <riseup-button v-if="!challengeStartDate && isChallengeOwner" class="start-challenge-button" :title="'קדימה!'" :action="startChallenge"
                       :loading="loading" :disabled="loading" :variant="'secondary'"/>
        <template v-if="!activeParticipant">
          <riseup-button class="join-challenge-button" title="הצטרפות לאתגר" variant="secondary" :action="openJoinChallangePopup"/>
        </template>
      </div>
      <div class="background-wrapper">
        <div class="hidden-background">
          <div v-if="challengeStartDate && activeParticipant" class="blue-background"/>
          <img :src="require('@/assets/challenge/RoundBottom.svg')" class="ellipse">
        </div>
      </div>
      <div class="card-wrapper">
        <div class="space" v-if="!challengeStartDate || !activeParticipant"/>
        <daily-nudge :daily-nudge-question="currentNudgeQuestion" :daily-nudge-answer="currentNudgeAnswer"
                     @daily-nudge-answered="handleNudgeAnswer" v-if="challengeStartDate && activeParticipant"/>
        <challenge-progress-card class="progress-card" v-if="challengeStartDate && activeParticipant" :headline="'הוצאת באתגר'"
                                 :challenge-goal="challengeAmount" :spent="participantSpent" :can-add-expense="!isCustomer"/>
      </div>
    </div>
    <challenge-progress-state class="progress-state" :days-left="daysLeft" :days-passed="daysPassed" :left-to-spend="leftToSpend"/>
    <div class="participants">
      <div class="headline">משתתפים באתגר</div>
      <div class="participants-grid">
        <participants-card v-for="(participant) in allParticipants" :key="participant.participantId" :participant="participant"/>
      </div>
    </div>
  </div>
</template>

<script>

import BaseUI from 'base-ui';
import { mapActions, mapState, mapGetters } from 'vuex';
import router from '@/router';
import ChallengeProgressCard
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/ChallengeProgressCard';
import ChallengeProgressState
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/ChallengeProgressState';
import RiseupHeader from '@/base-components/app-header/RiseupHeader';
import ParticipantsCard
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/ParticipantsCard';
import JoinChallengePopup
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/JoinChallengePopup';
import Segment from '@/Segment';
import AlreadyInChallengePopup
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/AlreadyInChallengePopup';
import {
  EATING_OUT_CHALLENGE_PROTOTYPE_V1,
} from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/constants';
import DailyNudge
  from '@/pages/responsive-pages/authenticated/multiplayer-experience/accountability-group-challenge/DailyNudge';

export default {
  name: 'ActiveChallengeDashboard',
  components: {
    RiseupButton: BaseUI.RiseupButton,
    ChallengeProgressCard,
    ChallengeProgressState,
    RiseupHeader,
    ParticipantsCard,
    DailyNudge,
  },
  data() {
    return {
      showCelebration: false,
    };
  },
  computed: {
    ...mapState('accountabilityChallenge', ['activeChallenge', 'activeParticipant', 'allParticipants',
      'isLoggedInCustomer', 'loading', 'ownerParticipantId']),
    ...mapGetters('accountabilityChallenge', ['participantName', 'participantSpent', 'participantIcon', 'isChallengeOwner',
      'challengeAmount', 'challengeStartDate', 'daysPassed', 'leftToSpend', 'daysLeft', 'isCustomer', 'participantId', 'currentNudgeQuestion',
      'currentNudgeAnswer']),
    description() {
      if (!this.activeParticipant) {
        return `בשבוע הקרוב נעקוב אחרי הוצאות האוכל בחוץ שלך. כדי לעמוד באתגר עליך להוציא פחות מ-₪${this.challengeAmount}.`;
      }
      if (this.challengeStartDate) {
        return 'כאן נעקוב אחרי ההוצאות שלך,'
            + '\n'
            + ' קטן עליך!';
      }
      if (this.isChallengeOwner) {
        return 'הגיעו כל החברים? אפשר להתחיל באתגר?';
      }
      return 'האתגר עדיין לא התחיל, אפשר להתעדכן בקבוצת הוואטסאפ.';
    },
    headline() {
      if (!this.activeParticipant) {
        return 'כיף שהגעת לאתגר האוכל בחוץ!';
      }
      if (this.challengeStartDate) {
        return `היי ${this.participantName}`;
      }
      if (this.isChallengeOwner) {
        return `היי ${this.participantName}, שמחים שפתחת את אתגר האוכל בחוץ.`;
      }
      return `היי ${this.participantName}, כיף שהצטרפת לאתגר האוכל בחוץ.`;
    },
  },
  created() {
    Segment.trackUserGot('EatingOutChallengeDashboardEntered',
      {
        participantId: this.participantId,
        isCustomer: this.isCustomer,
        challengeId: this.ownerParticipantId,
        shareName: EATING_OUT_CHALLENGE_PROTOTYPE_V1,
      });
  },
  methods: {
    ...mapActions('modalRootStore', ['openModal', 'closeModal']),
    ...mapActions('accountabilityChallenge', ['startChallenge', 'answerNudgeQuestion']),
    async handleNudgeAnswer(answer) {
      await this.answerNudgeQuestion(answer);
      this.showCelebration = answer;
    },
    join() {
      router.push({ path: '/signup' });
      this.closeModal();
    },
    redirectToLogin() {
      Segment.trackUserInteraction('EatingOutChallengeAlreadyAMemberClicked', {
        challengeId: this.ownerParticipantId,
        participantId: this.participantId,
        shareName: EATING_OUT_CHALLENGE_PROTOTYPE_V1,
      });
      router.push({ path: '/login', query: { ...this.$route.query, redirectTo: `${this.$route.path.substring(1)}/li` } });
    },
    openJoinChallangePopup() {
      Segment.trackUserInteraction('EatingOutChallengeJoinChallengeClicked',
        { challengeId: this.ownerParticipantId, shareName: EATING_OUT_CHALLENGE_PROTOTYPE_V1 });
      this.openModal({
        component: JoinChallengePopup,
      });
    },
    openAlreadyJoinedChallengePopup() {
      Segment.trackUserInteraction('EatingOutChallengeAlreadyJoinedChallengeClicked',
        { challengeId: this.ownerParticipantId, shareName: EATING_OUT_CHALLENGE_PROTOTYPE_V1 });
      this.openModal({
        component: AlreadyInChallengePopup,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~base-ui/src/scss/riseup-colors';
@import '../../../../../scss/mixins';
.active-challenge-dashboard {
  .top {
    position: relative;
    .celebration-animation {
      position: absolute;
      z-index: 1;
      top: 0;
      left: 0;
      width: 100%;
      height: 495px;
    }
    .header {
      @include responsive-padding;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 24px;
      background-color: $riseup_blue;
      color: $riseup_white;
      text-align: center;
      .icon {
        width: 40px;
        height: 56px;
      }
      .headline {
        margin: 15px 0 40px;
        font-weight: bold;
        font-size: 18px;
        white-space: pre-wrap;
        line-height: 25.5px;
      }
      .start-challenge-button {
        width: 100%;
        height: 60px;
        margin-bottom: 30px;
      }
      .join-challenge-button {
        margin: 24px 0;
        width: 100%;
        height: 60px;
      }
      .already-in-challenge {
        margin-top: 30px;
        cursor: pointer;
        color: $riseup-white;
        font-family: SimplerPro;
        font-size: 16px;
      }
    }
    .background-wrapper {
      position: relative;
      .hidden-background {
        position: absolute;
        top: 0;
        .blue-background {
          height: 110px;
          background-color: $riseup-blue;
        }
        .ellipse {
          width: 100%;
        }
      }
    }
    .card-wrapper {
      @include responsive-padding;
      display: flex;
      flex-direction: column;
      align-items: center;
      .space {
        height: 40px;
      }
      .progress-card {
        margin-top: 40px;
      }
    }

  }

  .progress-state {
    margin-top: 40px;
  }

  .tool-tip {
    @include responsive-padding;
    margin-top: 24px;
    font-size: 14px;
    line-height: 20px;
    color: $riseup-black;
    font-family: SimplerPro;
    .tip-headline {
      font-weight: bold;
    }
  }

  .participants {
    @include responsive-padding;
    margin-top: 24px;
    font-size: 18px;
    .headline {
      margin-bottom: 30px;
      font-weight: bold;
      font-size: 18px;
    }
    .participants-grid {
      display: grid;
      justify-content: space-between;
      grid-template-columns: auto auto;
      row-gap: 30px;
      padding-bottom: 24px;
    }
  }

  .links {
    margin: 24px 0;
  }

  .join-riseup {
    margin-top: 24px;
    @include responsive-padding;
    .headline {
      margin-bottom: 24px;
      text-align: center;
    }
    .join-button {
      margin-bottom: 24px;
    }
  }
}

</style>
